import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '../../components/feature/SiteLayout';
import SectionHeading from '../../components/base/SectionHeading';
import MovieCard from '../../components/base/MovieCard';
import PlayerDemo from './components/PlayerDemo';
import FavoriteButton from './components/FavoriteButton';
import { movies } from '../../mocks/movies';
import { formatViews, getMovieById, getRelatedMovies } from '../../utils/movies';

export default function Detail() {
  const { id = '' } = useParams();
  const movie = getMovieById(movies, id);

  useEffect(() => {
    if (movie) {
      document.title = `${movie.title}高清中文字幕在线观看 - 中文字幕永久在线`;
      const desc = document.querySelector('meta[name="description"]');
      if (desc)
        desc.setAttribute(
          'content',
          `《${movie.title}》${movie.year}年${movie.region}${movie.type === 'movie' ? '电影' : '电视剧'}，类型：${movie.genres.join('、')}。${movie.synopsis.slice(0, 60)}...`,
        );
    }
  }, [movie]);

  if (!movie) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-8 py-24 flex flex-col items-center text-center">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
            <i className="ri-file-unknow-line text-3xl" aria-hidden="true"></i>
          </span>
          <h1 className="font-heading font-bold text-xl text-foreground-900 mb-2">未找到该影视内容</h1>
          <p className="text-sm text-foreground-500 mb-6">链接可能已失效，或该影视暂未收录</p>
          <Link
            to="/"
            className="flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
          >
            <i className="ri-home-4-line" aria-hidden="true"></i>
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedMovies(movies, movie, 4);

  const metaItems = [
    { label: '导演', value: movie.director },
    { label: '主演', value: movie.actors.join(' / ') },
    { label: '年代', value: `${movie.year}` },
    { label: '地区', value: movie.region },
    { label: '片长', value: movie.duration },
    { label: '类型', value: movie.genres.join(' · ') },
  ];

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 py-6 md:py-8 bg-background-50">
        <nav className="flex items-center flex-wrap gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-600 cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300" aria-hidden="true"></i>
          <Link
            to={movie.type === 'movie' ? '/category/movie' : '/category/tv'}
            className="hover:text-primary-600 cursor-pointer"
          >
            {movie.type === 'movie' ? '电影' : '电视剧'}
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300" aria-hidden="true"></i>
          <span className="text-foreground-800">{movie.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 md:gap-10 mb-10">
          <div className="w-full max-w-[300px] mx-auto lg:mx-0">
            <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
              <img
                src={movie.cover}
                alt={`${movie.title}高清中文字幕在线观看`}
                title={`${movie.title} - 中文字幕永久在线`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <span className="px-3 py-1 rounded-md bg-primary-50 text-primary-700 text-xs font-medium">
                {movie.type === 'movie' ? '高清电影' : '电视剧集'}
              </span>
              <span className="flex items-center gap-1 px-3 py-1 rounded-md bg-accent-100 text-accent-800 text-xs font-semibold">
                <i className="ri-star-fill" aria-hidden="true"></i>
                {movie.rating.toFixed(1)} 分
              </span>
              <span className="px-3 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs">中文字幕</span>
            </div>
          </div>

          <div className="min-w-0">
            <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-950 mb-3">{movie.title}</h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-600 mb-5">
              <span className="flex items-center gap-1">
                <i className="ri-fire-line text-accent-500" aria-hidden="true"></i>
                {formatViews(movie.views)} 次观看
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" aria-hidden="true"></i>
                更新于 {movie.updatedAt}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-closed-captioning-line" aria-hidden="true"></i>
                中文字幕永久在线
              </span>
            </div>

            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">{movie.synopsis}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mb-6">
              {metaItems.map((item) => (
                <div key={item.label} className="flex gap-3 text-sm">
                  <span className="text-foreground-400 shrink-0 w-12">{item.label}</span>
                  <span className="text-foreground-800 min-w-0">{item.value}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <a
                href="#player"
                className="flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer"
              >
                <i className="ri-play-fill text-lg" aria-hidden="true"></i>
                立即观看
              </a>
              <FavoriteButton movieId={movie.id} />
              <button
                type="button"
                className="flex items-center gap-2 h-11 px-6 rounded-md border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
                aria-label="分享链接"
              >
                <i className="ri-share-forward-line" aria-hidden="true"></i>
                分享
              </button>
            </div>

            <p className="text-xs text-foreground-400 mt-3">
              分享链接：https://lvyinzb.asia/detail/{movie.id}（演示文本）
            </p>
          </div>
        </div>

        <div id="player" className="mb-12 scroll-mt-24">
          <SectionHeading title="在线试看演示" subtitle="播放器界面演示，仅展示交互效果，不提供真实视频流" />
          <PlayerDemo title={movie.title} cover={movie.cover} />
        </div>

        <div className="mb-12">
          <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-950 mb-4">所属分类与标签</h2>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={movie.type === 'movie' ? '/category/movie' : '/category/tv'}
              className="px-3.5 py-1.5 rounded-md bg-secondary-100 text-secondary-900 text-sm hover:bg-secondary-200 transition-colors cursor-pointer"
            >
              {movie.type === 'movie' ? '电影' : '电视剧'}
            </Link>
            {movie.genres.map((genre) => (
              <Link
                key={genre}
                to={`/category/${encodeURIComponent(genre)}`}
                className="px-3.5 py-1.5 rounded-md bg-background-100 text-foreground-700 text-sm hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
              >
                {genre}
              </Link>
            ))}
            {movie.tags
              .filter((tag) => !movie.genres.includes(tag))
              .map((tag) => (
                <span
                  key={tag}
                  className="px-3.5 py-1.5 rounded-md bg-accent-50 text-accent-800 text-sm"
                >
                  #{tag}
                </span>
              ))}
          </div>
        </div>

        <section>
          <SectionHeading title="同类型相关推荐" subtitle="根据分类与标签为你匹配的相似作品" moreTo="/category/movie" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} />
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}